import { CREATE_NOTICE } from "../../../routes";
import { POLL_MAX_OPTIONS } from "../../polls/types";
import { pollChoicesProblem, pollQuestionProblem } from "../../notices/noticeRules";
import type { WizardHandover, WizardInitialFormData } from "../../notices/wizardSession";
import { capitalize, quoted } from "../catalogue/wording";
import { dayLabel } from "../dateRange";
import { isFiller, trimFiller } from "../fillerWords";
import { findDay, readTaskDetails, resolveDay, tidy, type DayKey } from "../taskDetails";
import { timeIn, timeLabel } from "../timeOfDay";
import { EVERYONE_WORDS, findRecipients, type Recipient } from "./recipients";
import {
  CALLED_WHO,
  CHOOSE_IT_THERE,
  dayHasPassed,
  withoutRemark,
  namesRecipient,
  readRecipient,
  recipientInstruction,
  spotRecipient,
  typed,
  whoQuestion as sharedWhoQuestion,
  withoutEndPunctuation,
} from "./sharedDetails";
import type { Question, TaskContext, TaskSchema } from "./taskConversation";

/** When a Poll closes: a day, and a time if one was said ("HH:MM"). */
type Closes = { day: DayKey; time?: string };

type PollDetails = {
  question?: string;
  choices?: string[];
  to?: Recipient;
  closes?: Closes;
};

/* The question */

const QUESTION_QUESTION: Question = { text: "What's the question?", choices: [] };
/** Starts like a question: "which day suits", "should we…". What a Poll is "about" is only its question when it is one. */
const QUESTION_WORD =
  /^(?:which|what|when|where|who|whom|whose|why|how|should|shall|can|could|will|would|do|does|did|is|are|was|were)\b/i;
/** "a poll asking which day suits", "a poll: which day suits?": what follows is the question. */
const ASKS = /\bask(?:ing|s)?\b|:/i;

/** The question as the wizard shows it: "which day suits" is "Which day suits?". */
function readQuestion(said: string): { value: string } | Question {
  const question = withoutRemark(said).replace(/^(?:the\s+)?(?:question|q)\s*(?:is\b|[:=])\s*/i, "")
    .replace(/\s+/g, " ")
    // "Which house will win? 🏆": the emoji after it isn't part of the question
    .replace(/(?:\s*[\p{Extended_Pictographic}\uFE0F])+$/u, "")
    .replace(/[\s.,:;-]+$/, "");
  if (pollQuestionProblem(question) || isFiller(question)) return QUESTION_QUESTION;
  return { value: /[?!]$/.test(question) ? capitalize(question) : `${capitalize(question)}?` };
}

/* The choices */

const CHOICES_QUESTION: Question = { text: "What are the choices? Separate them with commas.", choices: [] };
const CHOICES_AGAIN = "What are the choices? Separate them with commas, like “Yes, No, Maybe”.";

/** "The four options are:", "Options:", "options": what's said before the choices themselves. */
const OPTIONS_LEAD = /^(?:the\s+)?(?:\w+\s+)?(?:options|choices|answers)\b\s*(?:are\b\s*:?|:)\s*|^(?:options|choices|answers)\s+/i;
/** "it's a yes/no question", "yes or no": the two choices that means. */
const YES_NO = /\byes\s*(?:\/|or|-)\s*no\b/i;

/** "Pizza, pasta, or salad." and "Pizza or pasta" as each choice, capitalized. */
function choicesIn(said: string): string[] {
  if (YES_NO.test(said) && !/[,;]/.test(said)) return ["Yes", "No"];
  // "I already gave them. Pride and Prejudice, …", "Ooh lovely! Koeksisters, …": a remark before the first choice
  const listed = typed(said).replace(/\s+🙏[.!]*$/u, "").replace(/\s+(?:pls|plz|please)[.!?]*$/i, "").replace(OPTIONS_LEAD, "").replace(/^\s*(?:\d+[.)]|[-•])\s+/gm, "");
  const firstComma = listed.search(/[,;\n]/);
  const beforeFirst = firstComma < 0 ? listed : listed.slice(0, firstComma);
  const remark = beforeFirst.match(/^.*[.!?]\s+/);
  const parts = (remark ? listed.slice(remark[0].length) : listed).split(/\s*[,;\n]\s*/);
  // "Durban, Drakensberg or Cape Town": the last one can be joined with "or"
  const each = parts.length > 1 ? [...parts.slice(0, -1), ...parts[parts.length - 1].split(/\s+(?:or|and)\s+/i)] : parts[0].split(/\s+(?:or|and)\s+|\s*\/\s*/i);
  return each
    .map((choice) => withoutEndPunctuation(choice.replace(/^(?:or|and)\s+/i, "")).replace(/^["“']|["”']$/g, ""))
    .filter(Boolean)
    .map(capitalize);
}

/** Choices the wizard won't take, with the reason, asked again. */
function choicesQuestion(choices: string[]): Question {
  if (choices.length === 0) return CHOICES_QUESTION;
  if (choices.length === 1) {
    return { text: `A Poll needs at least 2 choices, and I only found ${quoted(choices[0])}. ${CHOICES_AGAIN}`, choices: [] };
  }
  if (choices.length > POLL_MAX_OPTIONS) {
    return {
      text: `A Poll can have up to ${POLL_MAX_OPTIONS} choices, and this one has ${choices.length}. ${CHOICES_AGAIN}`,
      choices: [],
    };
  }
  const twice = choices.find((choice, i) => choices.findIndex((c) => c.toLowerCase() === choice.toLowerCase()) !== i);
  return { text: `Each choice needs to be different, and ${quoted(twice ?? choices[0])} is there twice. ${CHOICES_AGAIN}`, choices: [] };
}

function readChoices(said: string): { value: string[] } | Question {
  const choices = choicesIn(said);
  return pollChoicesProblem(choices) ? choicesQuestion(choices) : { value: choices };
}

/* When it closes */

/** "close it", "closes", "voting closes": never "close" alone, so "Should we close early?" is a question. */
const SAYS_CLOSES = "(?:(?:make\\s+it\\s+close|can\\s+it\\s+close(?=\\s+(?:on|by|at)\\b))|close\\s+(?:it|the\\s+poll|voting)|(?:(?:it|voting|the\\s+poll)\\s+)?(?:(?:should|must)\\s+close|closes|closing)|deadline(?:\\s+is)?)";
/** "close it on Friday at 5pm" in an answer, up to whatever's said next. */
const CLOSES_PHRASE = new RegExp(`\\s*\\b(?:and\\s+)?${SAYS_CLOSES}\\s+(?:on\\s+|at\\s+|by\\s+)?([^,.;!?]+)`, "i");
/** "close it on Friday", as a whole answer. */
const CLOSES_ALONE = new RegExp(`^(?:(?:please|and|also|actually|oh)[\\s,]+)*${SAYS_CLOSES}\\s+`, "i");
const CLOSES_EXAMPLE = "Try “close it on Friday” or “Friday at 5pm”.";

/** When it closes, from "Friday at 5pm" or "close it on Friday": one it can read that hasn't passed. */
function readCloses(said: string, org: TaskContext): { value: Closes } | Question {
  const phrase = withoutEndPunctuation(said).replace(CLOSES_ALONE, "");
  if (/\b(?:last|previous)\s+(?:monday|tuesday|wednesday|thursday|friday|saturday|sunday|week|month|year)\b/i.test(phrase)) return { text: `That day has already passed. ${CLOSES_EXAMPLE}`, choices: [] };
  const day = findDay(phrase);
  if (!day) return { text: `I couldn't tell when it closes. ${CLOSES_EXAMPLE}`, choices: [] };
  if (dayHasPassed(day, org.today)) return { text: `That day has already passed. ${CLOSES_EXAMPLE}`, choices: [] };
  const time = timeIn(phrase)?.time;
  return { value: time ? { day, time } : { day } };
}

function spotCloses(said: string, org: TaskContext) {
  const found = said.match(CLOSES_PHRASE);
  if (found?.index === undefined) return undefined;
  const read = readCloses(found[1], org);
  if (!("value" in read)) return undefined;
  return { value: read.value, rest: tidy(said.slice(0, found.index) + " " + said.slice(found.index + found[0].length)) };
}

/** The moment it closes: the end of the day unless a time was said, so it's open all of that day. */
function closingMoment({ day, time = "23:59" }: Closes, today: Date) {
  const date = resolveDay(day, today);
  const [hour, minutes] = time.split(":").map(Number);
  return new Date(date.getFullYear(), date.getMonth(), date.getDate(), hour, minutes);
}

/* The request */

/**
 * The details a request for a Poll gives. When it closes is read out first, so what's
 * left says who it's for and what it asks: "Create a poll for Grade 3 asking which day
 * suits" asks "Which day suits?". What it's "about" is only its question when it is one.
 */
/** "…, yes or no", "… (yes/no)": a Poll whose choices are Yes and No. */
const YES_OR_NO_AT_END = /[\s,.;:(-]*\byes\s*(?:or|\/)\s*no\b\)?[\s.?!]*$/i;

/** "if they can help at the fun day": the question as parents read it, "Can you help at the fun day?" */
function asParentsWouldRead(said: string): string | undefined {
  const found = said
    .trim()
    .replace(/[\s,.!?]+$/, "")
    .match(/^(?:(?:asking|ask)\s+)?(?:(?:the\s+)?(?:parents|families|everyone)\s+)?(?:if|whether)\s+(?:they|parents|families|you)\s+(can|could|will|would|are)\s+(.+)$/i);
  return found ? `${capitalize(found[1].toLowerCase())} you ${found[2]}?` : undefined;
}

function readRequest(request: string, org: TaskContext) {
  const closes = spotCloses(request, org);
  const rest = trimFiller(withoutEndPunctuation(closes?.rest ?? request));
  const { said } = readTaskDetails(rest);
  // Read the question before generic topic extraction: its colon can introduce choices,
  // while the audience may precede the question rather than follow it.
  const body = rest.replace(/^.*?\bpoll\b[\s:.-]*/i, "").replace(/\b(?:question|q)\s*(?:is\b|[:=])\s*(?=(?:which|what|when|where|who|how|should|can|will|is|are)\b)/i, "");
  const questionStart = body.search(/\b(?:which|what|when|where|who|why|how|should|shall|can|could|will|would|do|does|did|is|are|was|were)\b/i);
  if (questionStart >= 0) {
    const prefix = body.slice(0, questionStart).trim().replace(/["“'‘]+$/, "").trim().replace(/\s+(?:asking|ask)\s*$/i, "").replace(/\s+(?:cheers|thanks|thank you|please|pls)\s*$/i, "");
    const audience = prefix.match(/\b(?:for|to)\s+(.+?)[\s:–—-]*$/i)?.[1]
      ?? ((findRecipients(recipientsOf(org), prefix.replace(/[\s:–—-]+$/, ""), org.organizationName).length === 1 || EVERYONE_WORDS.test(prefix.replace(/[\s:–—-]+$/, "").toLowerCase())) ? prefix.replace(/[\s:–—-]+$/, "") : undefined);
    const split = questionThenChoices(body.slice(questionStart), org);
    if (split.question) return {
      ...split, to: split.to ?? audience ?? said.audience,
      closes: closes && { value: closes.value },
    };
  }
  const asks = said.topic && (ASKS.test(rest.slice(0, rest.indexOf(said.topic))) || QUESTION_WORD.test(said.topic));
  // "a poll for parents: whatsapp or email?" read as "for parents whatsapp or email": the question, with its choices
  const asksBetween =
    !said.topic && said.audience && /\s+or\s+/i.test(said.audience) && !findRecipients(recipientsOf(org), said.audience).length
      ? said.audience.replace(/^(?:the\s+)?(?:parents|families|everyone|staff|learners)\s+/i, "")
      : undefined;
  if (asksBetween) {
    const question = `${capitalize(withoutEndPunctuation(asksBetween))}?`;
    return { question, choices: asksBetween, closes: closes && { value: closes.value } };
  }
  // "asking parents if they can help at the fun day, yes or no": Yes and No are the choices, and the
  // question is asked for unless the rest is one ("Can you help at the fun day? yes or no")
  const yesNo = said.topic?.match(YES_OR_NO_AT_END);
  if (said.topic && yesNo?.index !== undefined) {
    const before = said.topic.slice(0, yesNo.index).trim();
    const question = /\?$/.test(before) && QUESTION_WORD.test(before) ? before : asParentsWouldRead(before);
    return { question, choices: "Yes, No", to: said.audience, closes: closes && { value: closes.value } };
  }
  const split = said.topic ? questionThenChoices(said.topic, org) : {};
  return {
    question: split.question ?? (asks && !split.choices ? said.topic : undefined),
    choices: split.choices,
    to: split.to ?? said.audience,
    closes: closes && { value: closes.value },
  };
}

/**
 * What a Poll is "about", when it says more than its question: "fav spirit week day? pj day, twin day",
 * "Has your child had the MMR booster? Options: Yes, No, Not sure.", "Can you attend? yes or no. testing.",
 * or only its choices: "Durban, Drakensberg or Cape Town?". The question ends at its question mark; after
 * it come the choices, and a last sentence that's only a Circle's name says who it goes to.
 */
function questionThenChoices(topic: string, org: TaskContext): { question?: string; choices?: string; to?: string } {
  const tail = topic.match(/(?:[.!?]\s+|,\s*|\s+[—–-]\s+)(?:send\s+(?:it\s+)?)?(?:to|for)\s+(.+?)[.!?]*$/i);
  if (tail?.index !== undefined) {
    const audience = trimFiller(withoutEndPunctuation(tail[1]));
    if (namesRecipient(audience, recipientsOf(org), org) || EVERYONE_WORDS.test(audience.toLowerCase())) {
      return { ...questionThenChoices(topic.slice(0, tail.index), org), to: audience };
    }
  }

  // "Which day suits you Choices: Monday, Tuesday. To Everyone": a "Choices:" label splits it, question mark or not
  const labelledChoices = topic.match(/^(.*?)[\s,.;]*\b(?:choices|options|answers)\s*[:=]\s*(.+)$/i);
  if (labelledChoices) {
    const asked = withoutEndPunctuation(labelledChoices[1].replace(/^question\s*[:=]\s*/i, "").trim());
    const parts = labelledChoices[2].trim().replace(/([.!?])\s+/g, "$1\n").split("\n").map((part) => part.trim()).filter(Boolean);
    const last = parts.length > 1 ? parts[parts.length - 1].replace(/^(?:send\s+it\s+)?(?:to|for)\s*:?\s+/i, "").replace(/[.!?]+$/, "") : "";
    const to = last && namesRecipient(last, recipientsOf(org), org) ? last : undefined;
    const listed = (to ? parts.slice(0, -1) : parts).join(" ").replace(/[.!?]+$/, "");
    if (choicesIn(listed).length >= 2) {
      return { ...(asked ? { question: `${capitalize(asked)}?` } : {}), choices: listed, ...(to ? { to } : {}) };
    }
  }
  const colonChoices = topic.match(/^(.+?):\s+(.+)$/s);
  if (colonChoices && !colonChoices[1].includes("?") && QUESTION_WORD.test(colonChoices[1]) && choicesIn(colonChoices[2]).length >= 2) {
    return { question: `${capitalize(withoutEndPunctuation(colonChoices[1]))}?`, choices: colonChoices[2] };
  }
  const comma = topic.indexOf(",");
  const questionMark = topic.indexOf("?");
  if (QUESTION_WORD.test(topic) && comma > 0 && (questionMark < 0 || questionMark >= topic.trim().length - 1)) {
    const choices = withoutEndPunctuation(topic.slice(comma + 1));
    if (choicesIn(choices).length >= 2) return { question: `${withoutEndPunctuation(topic.slice(0, comma))}?`, choices };
  }
  const mark = topic.indexOf("?");
  const before = mark < 0 ? topic : topic.slice(0, mark + 1);
  // "Which house will win? 🏆": nothing but an emoji after it says nothing more
  const afterMark = mark < 0 || !/[\p{L}\p{N}]/u.test(topic.slice(mark + 1)) ? "" : topic.slice(mark + 1).trim().replace(/^["”'’]\s*/, "").replace(/^with\s+(?:the\s+)?(?:options|choices|answers)\s*:?\s*/i, "");
  // "Durban, Drakensberg or Cape Town?": a list, not a question
  // "school lunch: pasta, rice or sandwiches": what it's about, then the choices; the question is asked
  const labelled = !afterMark ? before.match(/^[^:]+:\s*(.+)$/) : null;
  if (labelled && choicesIn(labelled[1]).length >= 2) {
    // "which day suits you: tuesday or thursday": the part before the colon is the question, when it is one
    const lead = before.slice(0, before.indexOf(":")).replace(/^(?:asking|ask)\s+/i, "").trim();
    const question = QUESTION_WORD.test(lead) ? `${capitalize(withoutEndPunctuation(lead))}?` : undefined;
    return { ...(question ? { question } : {}), choices: labelled[1] };
  }
  if (!afterMark && !QUESTION_WORD.test(before) && choicesIn(before).length >= 3) return { choices: before };
  if (!afterMark) return QUESTION_WORD.test(before) ? { question: before } : {};
  const sentences = afterMark.replace(/([.!?])\s+/g, "$1\n").split("\n").map((part) => part.trim()).filter(Boolean);
  const last = sentences[sentences.length - 1];
  // "… Choices: Monday, Tuesday. To Everyone": the last sentence names who it's for, with or without "to"
  const lastName = last?.replace(/^(?:send\s+it\s+)?(?:to|for)\s*:?\s+/i, "");
  const named = lastName && namesRecipient(lastName, recipientsOf(org), org) ? lastName.replace(/[.!]+$/, "") : undefined;
  const listed = (named ? sentences.slice(0, -1) : sentences.length > 1 && choicesIn(sentences[0]).length >= 2 ? sentences.slice(0, 1) : sentences).join(" ");
  const choices = listed && choicesIn(listed).length >= 2 ? listed : undefined;
  return { question: before, ...(choices ? { choices } : {}), ...(named ? { to: named } : {}) };
}

/* The schema */

/** A Poll goes to Everyone or a Circle, never one User. */
const recipientsOf = (org: TaskContext) => org.everyoneAndCircles;
const whoQuestion = (org: TaskContext) => sharedWhoQuestion("Who should it go to?", recipientsOf(org));

/** Where the wizard puts who it goes to; left to be chosen there when it isn't known. */
const audienceOf = (to: Recipient | undefined): WizardInitialFormData =>
  to?.kind === "circle"
    ? { audience: "subcircle", targetSubcircle: to.id }
    : { audience: to?.kind === "everyone" ? "circle" : "" };

/**
 * A Poll: a question, its choices and who it goes to, asked in that order. It has no
 * title or message: the question is its title. When it closes is only taken when said.
 */
export const pollTask: TaskSchema = {
  task: "poll",
  details: [
    {
      name: "question",
      required: true,
      called: /^(?:question|title)$/i,
      step: "poll",
      question: () => QUESTION_QUESTION,
      read: readQuestion,
      // "Which trip is best for Grade 7?" asks which trip is best, of Grade 7
      alsoReads: ["closes", "to"],
    },
    {
      name: "choices",
      required: true,
      called: /^(?:choices|options|answers)$/i,
      step: "poll",
      question: () => CHOICES_QUESTION,
      // "the choices are yes, no" and "choices yes no maybe"
      isValue: (said) => /^(?:the\s+)?choices\s+(?:are|were)\b/i.test(said) || /^choices\s+(?!for\b|about\b)[^?]+$/i.test(said),
      instruction: (said) => {
        const found = said.match(/^(?:the\s+)?choices\s+(?:(?:are|were)\s+)?(.+)$/i);
        if (!found) return undefined;
        const list = found[1].replace(/\s*,?\s*not\s+.+$/i, "");
        // "yes no maybe": short words with no commas between them are each a choice
        const words = list.trim().split(/\s+/);
        const bare = !/[,;/]|\bor\b/i.test(list) && words.length >= 2 && words.length <= POLL_MAX_OPTIONS && words.every((w) => w.length <= 8);
        return bare ? words.join(", ") : list;
      },
      read: readChoices,
      // Choices can name Circles ("Lunch for Grade 3, Lunch for Grade 4"), so who it goes to isn't read from them
      alsoReads: ["closes"],
    },
    {
      name: "to",
      required: true,
      called: CALLED_WHO,
      step: "review",
      question: (org) => whoQuestion(org)(),
      read: (said, org) => readRecipient(said, recipientsOf(org), org, whoQuestion(org)),
      spot: (said, org) => spotRecipient(said, recipientsOf(org)),
      instruction: (said, org) => recipientInstruction(said, recipientsOf(org), org),
      alsoReads: ["closes"],
      isValue: (said, org) => namesRecipient(said, recipientsOf(org), org),
      writtenThere: CHOOSE_IT_THERE,
      requiresExplicitSkip: true,
    },
    {
      name: "closes",
      called: /^(?:clos(?:e|es|ing)(?:\s+(?:date|day|time))?|deadline|when it closes)$/i,
      read: readCloses,
      spot: spotCloses,
      instruction: (said) => (CLOSES_ALONE.test(typed(said)) ? said : undefined),
    },
  ],
  cannotStart: (org) =>
    org.everyoneAndCircles.length === 0 ? "You don't have any Circles to send a Poll to yet." : undefined,
  saidIn: (said, request, org) => (request ? readRequest(request, org) : { to: said.audience }),
  summary: (details, org) => {
    const { question, choices, to, closes } = details as PollDetails;
    const closesOn = closes && dayLabel(resolveDay(closes.day, org.today));
    return [
      ...(question ? [{ label: "Question", value: question }] : []),
      ...(choices ? [{ label: "Choices", value: choices.join("\n") }] : []),
      { label: "To", value: to?.name ?? "choose on the next screen" },
      ...(closes ? [{ label: "Closes", value: closes.time ? `${closesOn}, ${timeLabel(closes.time)}` : `${closesOn}` }] : []),
    ];
  },
  handover: (details, org, { startAt = "review" }) => {
    const { question = "", choices, to, closes } = details as PollDetails;
    const initialFormData: WizardInitialFormData = {
      title: question,
      ...audienceOf(to),
      ...(choices ? { pollOptions: choices.map((label, order) => ({ optionId: `opt-${order}`, label, order })) } : {}),
      ...(closes ? { pollClosesAt: closingMoment(closes, org.today).toISOString() } : {}),
    };
    const state: WizardHandover = { prefillNotice: { noticeType: "poll", startAt, initialFormData } };
    return { pathname: CREATE_NOTICE, state };
  },
  readyText: "Here's your Poll. Nothing is sent until you press Publish.",
  handedOverText: "Here it is. Nothing is sent until you press Publish.",
  fillInThereText: "No problem, I'll open the Poll so you can fill that in there.",
};
